/**
 * CONSAI Business App - Authentication Context Provider
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);

  // Check client authentication
  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      if (response.data) {
        setUser(response.data);
        setIsAuthenticated(true);
        return response.data;
      }
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
    }
    return null;
  }, []);

  // Check admin authentication
  const checkAdminAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/auth/me`, {
        withCredentials: true
      });
      if (response.data) {
        setAdmin(response.data);
        setIsAdminAuthenticated(true);
        return response.data;
      }
    } catch (error) {
      setAdmin(null);
      setIsAdminAuthenticated(false);
    }
    return null;
  }, []);

  // Initial auth check
  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      await Promise.all([checkAuth(), checkAdminAuth()]);
      setLoading(false);
    };
    initAuth();
  }, [checkAuth, checkAdminAuth]);

  // Client login
  const login = async (emailOrMobile, password, rememberMe = false) => {
    const response = await axios.post(
      `${API_URL}/api/auth/login`,
      { email_or_mobile: emailOrMobile, password, remember_me: rememberMe },
      { withCredentials: true }
    );
    if (response.data.success) {
      setUser(response.data.user);
      setIsAuthenticated(true);
    }
    return response.data;
  };

  // Client signup
  const signup = async (userData) => {
    const response = await axios.post(
      `${API_URL}/api/auth/signup`,
      userData,
      { withCredentials: true }
    );
    if (response.data.success) {
      setUser(response.data.user);
      setIsAuthenticated(true);
    }
    return response.data;
  };

  // Google OAuth for client
  const googleAuth = async (sessionId, languagePreference = 'english') => {
    const response = await axios.post(
      `${API_URL}/api/auth/signup/google`,
      { session_id: sessionId, language_preference: languagePreference },
      { withCredentials: true }
    );
    if (response.data.success) {
      setUser(response.data.user);
      setIsAuthenticated(true);
    }
    return response.data;
  };

  // Google OAuth for admin
  const adminGoogleAuth = async (sessionId) => {
    const response = await axios.post(
      `${API_URL}/api/admin/auth/google`,
      { session_id: sessionId },
      { withCredentials: true }
    );
    if (response.data.success) {
      setAdmin(response.data.admin);
      setIsAdminAuthenticated(true);
    }
    return response.data;
  };

  // Client logout
  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setIsAuthenticated(false);
  };

  // Admin logout
  const adminLogout = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Admin logout error:', error);
    }
    setAdmin(null);
    setIsAdminAuthenticated(false);
  };

  // Update user profile
  const updateProfile = async (profileData) => {
    const response = await axios.put(
      `${API_URL}/api/profile/`,
      profileData,
      { withCredentials: true }
    );
    if (response.data) {
      setUser(response.data);
    }
    return response.data;
  };

  // Change password
  const changePassword = async (currentPassword, newPassword) => {
    const response = await axios.post(
      `${API_URL}/api/profile/change-password`,
      { current_password: currentPassword, new_password: newPassword },
      { withCredentials: true }
    );
    return response.data;
  };

  // Forgot password
  const forgotPassword = async (emailOrMobile) => {
    const response = await axios.post(
      `${API_URL}/api/auth/forgot-password`,
      { email_or_mobile: emailOrMobile }
    );
    return response.data;
  };

  // Verify OTP
  const verifyOtp = async (mobileNumber, otp) => {
    const response = await axios.post(
      `${API_URL}/api/auth/verify-otp`,
      { mobile_number: mobileNumber, otp }
    );
    return response.data;
  };

  // Send OTP
  const sendOtp = async (mobileNumber) => {
    const response = await axios.post(
      `${API_URL}/api/auth/send-otp`,
      { mobile_number: mobileNumber }
    );
    return response.data;
  };

  // Get plans
  const getPlans = async () => {
    const response = await axios.get(`${API_URL}/api/plans`);
    return response.data;
  };

  // Check plan access
  const checkPlanAccess = async (feature) => {
    const response = await axios.get(
      `${API_URL}/api/plan-access/${feature}`,
      { withCredentials: true }
    );
    return response.data;
  };

  // Check if user's plan has expired
  const isPlanExpired = () => {
    if (!user) return false;
    
    // Check if plan_expires_at exists and has passed
    if (user.plan_expires_at) {
      const expiryDate = new Date(user.plan_expires_at);
      const now = new Date();
      return expiryDate < now;
    }
    
    // If no expiry date and user is on 'expired' plan
    if (user.current_plan === 'expired') return true;
    
    return false;
  };

  // Refresh user data
  const refreshUser = async () => {
    await checkAuth();
  };

  const value = {
    user,
    admin,
    loading,
    isAuthenticated,
    isAdminAuthenticated,
    login,
    signup,
    logout,
    googleAuth,
    adminGoogleAuth,
    adminLogout,
    updateProfile,
    changePassword,
    forgotPassword,
    verifyOtp,
    sendOtp,
    getPlans,
    checkPlanAccess,
    isPlanExpired,
    checkAuth,
    checkAdminAuth,
    refreshUser,
    setUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
